/**
 * The dashboard's support endpoints, typed — Task 13.1, requirements 9.3, 10.1, and 15.1–15.5.
 *
 * Endpoint methods only; request state lives in `hooks.ts`, the same split `content/api.ts` uses. Every
 * response is parsed through the contract the API parses it with on the way out, so a shape change is a
 * failed request rather than an `undefined` in a table cell.
 *
 * ## Support is not a content type
 *
 * The routes are `/api/admin/support-requests/...`, deliberately outside `/api/admin/content/:type`. Task 6
 * kept it that way on the server and this keeps it that way in the client: there is no catalog entry, no
 * `ContentType`, and no shared list/detail machinery. A support request has a status vocabulary, an
 * assignee, notes, and events that no content row has, and pretending otherwise is how a seventh content
 * type appears.
 *
 * ## Opaque identifiers only
 *
 * Paths carry the `publicId` UUID, never the serial primary key — the API never exposes one. `encodeURIComponent`
 * on a value the contract has already constrained to a UUID is belt and braces, and costs nothing.
 *
 * ## Order is the server's
 *
 * The queue arrives in a deterministic order the API chose, and no method here sorts, reverses, or reorders.
 * The same rule the content list follows, for the same reason: two orderings is one too many.
 */
import {
  supportRequestDetailSchema,
  supportRequestListSchema,
  supportRequestNoteSchema,
  supportAssignmentTransitionSchema,
  supportRetentionHoldTransitionSchema,
  supportStatusTransitionSchema,
  type SupportAssignmentChange,
  type SupportAssignmentTransition,
  type SupportNoteInput,
  type SupportRequestDetail,
  type SupportRequestList,
  type SupportRequestListQuery,
  type SupportRequestNote,
  type SupportRetentionHoldInput,
  type SupportRetentionHoldTransition,
  type SupportStatusChange,
  type SupportStatusTransition,
} from '@apuestapr/contracts';

import type { ApiClient, ApiResult } from '../api/client.ts';

export const supportRequestsPath = '/api/admin/support-requests';

const requestPath = (publicId: string): string =>
  `${supportRequestsPath}/${encodeURIComponent(publicId)}`;

/**
 * The list query, as the API's own parser expects to receive it.
 *
 * Only supplied filters are sent. `page` and `pageSize` have contract defaults, and sending them
 * unconditionally would make "the default" a thing the browser decides — so an absent value stays absent and
 * the server applies its own. `assignee` is a union of a positive id and the literal `unassigned`, which is
 * why it is stringified rather than coerced.
 */
export const supportListSearch = (query: SupportRequestListQuery | undefined): string => {
  if (query === undefined) return '';
  const parameters = new URLSearchParams();
  if (query.page !== undefined) parameters.set('page', String(query.page));
  if (query.pageSize !== undefined) parameters.set('pageSize', String(query.pageSize));
  if (query.status !== undefined) parameters.set('status', query.status);
  if (query.category !== undefined) parameters.set('category', query.category);
  if (query.assignee !== undefined) parameters.set('assignee', String(query.assignee));
  if (query.search !== undefined) parameters.set('search', query.search);
  const search = parameters.toString();
  return search === '' ? '' : `?${search}`;
};

export interface SupportAdminApi {
  readonly list: (query?: SupportRequestListQuery) => Promise<ApiResult<SupportRequestList>>;
  readonly detail: (publicId: string) => Promise<ApiResult<SupportRequestDetail>>;
  readonly changeStatus: (
    publicId: string,
    input: SupportStatusChange,
    csrfToken: string,
  ) => Promise<ApiResult<SupportStatusTransition>>;
  readonly changeAssignment: (
    publicId: string,
    input: SupportAssignmentChange,
    csrfToken: string,
  ) => Promise<ApiResult<SupportAssignmentTransition>>;
  readonly addNote: (
    publicId: string,
    input: SupportNoteInput,
    csrfToken: string,
  ) => Promise<ApiResult<SupportRequestNote>>;
  readonly changeRetentionHold: (
    publicId: string,
    input: SupportRetentionHoldInput,
    csrfToken: string,
  ) => Promise<ApiResult<SupportRetentionHoldTransition>>;
}

export const createSupportAdminApi = (client: ApiClient): SupportAdminApi => ({
  list: (query) =>
    client.get(`${supportRequestsPath}${supportListSearch(query)}`, supportRequestListSchema),
  detail: (publicId) => client.get(requestPath(publicId), supportRequestDetailSchema),
  changeStatus: (publicId, input, csrfToken) =>
    client.send('PATCH', `${requestPath(publicId)}/status`, supportStatusTransitionSchema, {
      body: input,
      csrfToken,
    }),
  changeAssignment: (publicId, input, csrfToken) =>
    client.send('PATCH', `${requestPath(publicId)}/assignment`, supportAssignmentTransitionSchema, {
      body: input,
      csrfToken,
    }),
  addNote: (publicId, input, csrfToken) =>
    client.send('POST', `${requestPath(publicId)}/notes`, supportRequestNoteSchema, {
      body: input,
      csrfToken,
    }),
  changeRetentionHold: (publicId, input, csrfToken) =>
    client.send(
      'PATCH',
      `${requestPath(publicId)}/retention-hold`,
      supportRetentionHoldTransitionSchema,
      { body: input, csrfToken },
    ),
});
